import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotification } from '../components/Notification';
import './AdminInvoiceView.css';

function AdminCreditInvoiceView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const notification = useNotification();

  const { data: creditInvoice, isLoading } = useQuery({
    queryKey: ['admin-credit-invoice', id],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('sessionId');
      
      const response = await fetch(
        `/api/admin/credit-invoices/${id}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(sessionId && { 'x-session-id': sessionId }),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Nepavyko gauti kreditinės sąskaitos');
      }

      return await response.json();
    },
    enabled: !!id,
  });

  // Gauti kreditinės sąskaitos HTML peržiūrai
  const { data: invoiceHtml } = useQuery({
    queryKey: ['admin-credit-invoice-html', id],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('sessionId');
      
      const response = await fetch(
        `/api/admin/credit-invoices/${id}/pdf?format=html`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(sessionId && { 'x-session-id': sessionId }),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Nepavyko gauti kreditinės sąskaitos HTML');
      }

      return await response.text();
    },
    enabled: !!id,
  });

  const handlePrint = async (format = 'pdf') => {
    try {
      const token = localStorage.getItem('token');
      const sessionId = localStorage.getItem('sessionId');
      
      const response = await fetch(
        `/api/admin/credit-invoices/${id}/pdf?format=${format}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            ...(sessionId && { 'x-session-id': sessionId }),
          },
        }
      );

      if (!response.ok) {
        throw new Error('PDF generavimas nepavyko');
      }

      if (format === 'pdf') {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `kreditine-saskaita-${creditInvoice?.credit_number || id}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        // HTML formatas - atidaryti naujame lange
        const html = await response.text();
        const newWindow = window.open('', '_blank');
        if (newWindow) {
          newWindow.document.write(html);
          newWindow.document.close();
        }
      }
    } catch (error) {
      notification.error('Nepavyko atsisiųsti dokumento. Patikrinkite serverio logus.');
    }
  };

  if (isLoading) {
    return <div className="admin-invoice-view-page container">Kraunama...</div>;
  }

  if (!creditInvoice) {
    return (
      <div className="admin-invoice-view-page container">
        <p>Kreditinė sąskaita nerasta</p>
        <button
          onClick={() => navigate('/admin/credit-invoices')}
          className="btn btn-primary"
        >
          Grįžti į sąrašą
        </button>
      </div>
    );
  }

  return (
    <div className="admin-invoice-view-page container">
      <div className="invoice-header">
        <div>
          <button
            onClick={() => navigate('/admin/credit-invoices')}
            className="btn btn-secondary"
          >
            ← Grįžti
          </button>
        </div>
        <div className="invoice-actions">
          <button
            onClick={() => handlePrint('html')}
            className="btn btn-secondary"
          >
            👁️ Peržiūrėti HTML
          </button>
          <button
            onClick={() => handlePrint('pdf')}
            className="btn btn-primary"
          >
            📄 Atsisiųsti PDF
          </button>
        </div>
      </div>

      {/* Kreditinės sąskaitos peržiūra */}
      <div className="invoice-preview-wrapper">
        {invoiceHtml ? (
          <iframe
            srcDoc={invoiceHtml}
            className="invoice-preview-iframe"
            title="Kreditinės sąskaitos peržiūra"
          />
        ) : (
          <div className="loading">Kraunama kreditinė sąskaita...</div>
        )}
      </div>
    </div>
  );
}

export default AdminCreditInvoiceView;
